import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import appConfig from '../config/app-config';
import tokenAwareFetcher from '../utils/tokenAwareFetcher';
import { collateralApi } from '../services/collateralApi';

const COMM_API = appConfig.apis.communicationApiEnv;
const commFetcher = tokenAwareFetcher(COMM_API.address, COMM_API.key);

interface UseDraweeConfirmationPageEditProps {
    show: boolean;
    draweeConfirmationId: string;
    draweeExternalId?: string;
    debtorName: string;
    debtorDocument: string;
    debtorStatus?: string;
    draweeStatus?: string;
    receivables: any[];
    operationCode: string;
    history?: any[];
    onRefresh?: () => void;
}

export default function useDraweeConfirmationPageEdit({
    show, draweeConfirmationId, draweeExternalId, debtorName, debtorDocument,
    debtorStatus, draweeStatus: initialDraweeStatus,
    receivables: initialReceivables, operationCode, history: initialHistory, onRefresh,
}: UseDraweeConfirmationPageEditProps) {
    const [contact, setContact] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [showCallForm, setShowCallForm] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [farolStatus, setFarolStatus] = useState(initialDraweeStatus || 'NONE');
    const [showActionMenu, setShowActionMenu] = useState<string | null>(null);
    const [notConfirmId, setNotConfirmId] = useState<string | null>(null);
    const [notConfirmObs, setNotConfirmObs] = useState('');
    const [notConfirmReason, setNotConfirmReason] = useState('');
    const [selectedReceivables, setSelectedReceivables] = useState<string[]>([]);
    const [showInteractionForm, setShowInteractionForm] = useState<'EMAIL' | 'WHATSAPP' | null>(null);
    const [showContactForm, setShowContactForm] = useState(false);
    const [showUndoConfirm, setShowUndoConfirm] = useState(false);
    const [receivables, setReceivables] = useState(initialReceivables);
    const [history, setHistory] = useState(initialHistory || []);
    const [dvStatus, setDvStatus] = useState(debtorStatus || 'NOT_STARTED');

    const NOT_CONFIRM_REASONS = ['Divergência de data', 'Divergência de valor', 'Sacado não reconhece dívida', 'Outro motivo'];

    const handleReasonChange = (reason: string) => {
        setNotConfirmReason(reason);
        if (reason !== 'Outro motivo') setNotConfirmObs(reason);
        else setNotConfirmObs('');
    };

    useEffect(() => {
        setReceivables(initialReceivables);
        setHistory(initialHistory || []);
        setDvStatus(debtorStatus || 'NOT_STARTED');
        setFarolStatus(initialDraweeStatus || 'NONE');
    }, [debtorDocument, debtorStatus, initialReceivables, initialHistory?.length, initialDraweeStatus, initialHistory]);

    const isValidated = dvStatus === 'CONFIRMED' || dvStatus === 'PARTIALLY_CONFIRMED' || dvStatus === 'REJECTED';
    const showSelectColumn = !isValidated && receivables.some(r => r.canReject);

    const totals = useMemo(() => {
        const confirmed = receivables.filter(r => r.status === 'VERIFIED');
        const notConfirmed = receivables.filter(r => r.status === 'NOT_CONFIRMED');
        const pending = receivables.filter(r => r.canReject);
        return {
            count: receivables.length,
            amount: receivables.reduce((s: number, r: any) => s + (r.amount || 0), 0),
            confirmedCount: confirmed.length,
            confirmedAmount: confirmed.reduce((s: number, r: any) => s + (r.amount || 0), 0),
            notConfirmedCount: notConfirmed.length,
            pendingCount: pending.length,
        };
    }, [receivables]);

    // Fetch contact from communication API
    useEffect(() => {
        if (!show) return;
        setContact(null);
        if (!draweeExternalId && !debtorDocument) return;

        setLoading(true);
        (async () => {
            try {
                let matchId = draweeExternalId;
                if (!matchId) {
                    const digits = debtorDocument.replace(/\D/g, '');
                    if (digits.length >= 3) {
                        const searchRes = await commFetcher.get(`/contacts/search?q=${encodeURIComponent(digits)}`);
                        const searchData = await searchRes.json();
                        const match = searchData.items?.find((c: any) => c.taxId?.replace(/\D/g, '') === digits);
                        if (match) matchId = match.id;
                    }
                }
                if (!matchId) { setLoading(false); return; }

                const res = await commFetcher.get(`/contacts/${matchId}/page-view`);
                const d = await res.json();
                const phones = (d.contactPoints || []).filter((cp: any) => (cp.type === 'PHONE' || cp.type === 'WHATSAPP') && cp.isActive).map((cp: any) => ({ id: cp.id, number: cp.valueFormatted, whatsapp: cp.type === 'WHATSAPP' }));
                const emails = (d.contactPoints || []).filter((cp: any) => cp.type === 'EMAIL' && cp.isActive).map((cp: any) => ({ id: cp.id, address: cp.valueFormatted }));
                const contactData = { id: d.id, name: d.fullName, status: d.status || 'NONE', phones, emails };
                setContact(contactData);
                setFarolStatus(d.status || 'NONE');
                // Silently assign contact if needed — do NOT trigger refresh to avoid infinite loop
                if (!draweeExternalId || d.status !== initialDraweeStatus) {
                    collateralApi.assignContact(draweeConfirmationId, d.id).catch(() => {});
                }
            } catch { setContact(null); }
            finally { setLoading(false); }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, draweeExternalId, debtorDocument, draweeConfirmationId]);

    const deals = useMemo(() => receivables.filter(r => r.canReject).map(r => ({
        id: r.id, operation: operationCode, number: r.titleNumber || r.receivableNumber, assignor: debtorName, value: String(r.amount), status: r.status,
    })), [receivables, operationCode, debtorName]);

    const handleCreateContact = useCallback(async () => { setShowContactForm(true); }, []);

    const handleContactCreated = useCallback(async (newContact: any) => {
        setShowContactForm(false);
        setContact({ id: newContact.id, name: debtorName, status: 'NONE', phones: [], emails: [] });
        await collateralApi.assignContact(draweeConfirmationId, newContact.id).catch(() => {});
        toast.success('Contato criado e vinculado');
        onRefresh?.();
    }, [debtorName, onRefresh, draweeConfirmationId]);

    const handleAction = (action: string) => {
        setShowActionMenu(null);
        if (action === 'CALL') setShowCallForm(true);
        else setShowInteractionForm(action as 'EMAIL' | 'WHATSAPP');
    };

    const handleValidateSelected = useCallback(async (channel: string) => {
        setShowActionMenu(null);
        const selectableIds = receivables.filter(r => r.canReject).map(r => r.id);
        const idsToValidate = selectedReceivables.length > 0 ? selectedReceivables : selectableIds;
        setProcessing(true);
        try {
            await collateralApi.confirmDrawee(draweeConfirmationId, { receivable_ids: idsToValidate, channel });
            const updatedReceivables = receivables.map(r =>
                idsToValidate.includes(r.id) && r.canReject ? { ...r, status: 'VERIFIED', statusFormatted: 'Confirmado', canReject: false } : r
            );
            setReceivables(updatedReceivables);
            const remainingCanReject = updatedReceivables.filter(r => r.canReject);
            if (remainingCanReject.length === 0) {
                const hasNotConfirmed = updatedReceivables.some(r => r.status === 'NOT_CONFIRMED' || r.status === 'FAILED');
                setDvStatus(hasNotConfirmed ? 'PARTIALLY_CONFIRMED' : 'CONFIRMED');
            }
            setHistory(h => [{ action: 'VALIDATED', channel, timestamp: new Date().toISOString() }, ...h]);
            setSelectedReceivables([]);
            toast.success(selectableIds.length === 0 ? 'Validação encerrada' : 'Recebíveis validados');
            onRefresh?.();
        } catch { toast.error('Erro ao validar'); }
        finally { setProcessing(false); }
    }, [selectedReceivables, receivables, onRefresh, draweeConfirmationId]);

    const handleUndoValidation = useCallback(async () => {
        setShowUndoConfirm(false);
        setProcessing(true);
        try {
            const result = await collateralApi.revokeDraweeConfirmation(draweeConfirmationId);
            if (result?.status) setDvStatus(result.status);
            setReceivables(prev => prev.map(r => ({
                ...r, status: 'PENDING', statusFormatted: 'Pendente', canReject: true, canRevokeRejection: false, canValidate: true, observation: null,
            })));
            setHistory(h => [{ action: 'UNDO_VALIDATION', timestamp: new Date().toISOString() }, ...h]);
            toast.success('Validação desfeita');
            onRefresh?.();
        } catch { toast.error('Erro ao desfazer validação'); }
        finally { setProcessing(false); }
    }, [draweeConfirmationId, onRefresh]);

    const handleMarkNotConfirmed = useCallback(async (receivableId: string) => {
        if (!notConfirmObs.trim()) { toast.warn('Preencha a observação'); return; }
        setProcessing(true);
        try {
            const result = await collateralApi.rejectReceivable(draweeConfirmationId, receivableId, notConfirmObs);
            setReceivables(prev => prev.map(r => r.id === receivableId ? { ...r, status: 'NOT_CONFIRMED', statusFormatted: 'Não confirmado', canReject: false, canRevokeRejection: true, observation: notConfirmObs } : r));
            if (result?.status) setDvStatus(result.status);
            setNotConfirmId(null); setNotConfirmObs('');
        } catch { toast.error('Erro ao marcar'); }
        finally { setProcessing(false); }
    }, [draweeConfirmationId, notConfirmObs]);

    const handleUnmarkNotConfirmed = useCallback(async (receivableId: string) => {
        setProcessing(true);
        try {
            const result = await collateralApi.revokeRejection(draweeConfirmationId, receivableId);
            setReceivables(prev => prev.map(r => r.id === receivableId ? { ...r, status: 'PENDING', statusFormatted: 'Pendente', canReject: true, canRevokeRejection: false, observation: null } : r));
            if (result?.status) setDvStatus(result.status);
        } catch { toast.error('Erro ao desmarcar'); }
        finally { setProcessing(false); }
    }, [draweeConfirmationId]);

    const onCallSuccess = useCallback(async (newActivity: any) => {
        setShowCallForm(false);
        const observation = [newActivity?.title, newActivity?.body].filter(Boolean).join('. ');
        setDvStatus(prev => prev === 'NOT_STARTED' ? 'IN_PROGRESS' : prev);
        setReceivables(prev => prev.map(r => r.canReject ? { ...r, canValidate: true } : r));
        setHistory(h => [{ action: 'CALL', timestamp: new Date().toISOString(), observation }, ...h]);
        toast.success('Ligação registrada!');
        await collateralApi.recordInteraction(draweeConfirmationId, 'CALL', observation).catch(() => {});
        onRefresh?.();
    }, [onRefresh, draweeConfirmationId]);

    const handleInteractionSuccess = useCallback(async (type: 'EMAIL' | 'WHATSAPP', newActivity: any) => {
        setShowInteractionForm(null);
        setDvStatus(prev => prev === 'NOT_STARTED' ? 'IN_PROGRESS' : prev);
        setReceivables(prev => prev.map(r => r.canReject ? { ...r, canValidate: true } : r));
        setHistory(h => [{ action: type, timestamp: new Date().toISOString() }, ...h]);
        toast.success(type === 'EMAIL' ? 'Email registrado' : 'WhatsApp registrado');
        await collateralApi.recordInteraction(draweeConfirmationId, type, newActivity?.body).catch(() => {});
        onRefresh?.();
    }, [onRefresh, draweeConfirmationId]);

    return {
        contact, loading, processing,
        showCallForm, setShowCallForm,
        farolStatus, setFarolStatus,
        showActionMenu, setShowActionMenu,
        notConfirmId, setNotConfirmId,
        notConfirmObs, setNotConfirmObs,
        notConfirmReason, setNotConfirmReason, NOT_CONFIRM_REASONS, handleReasonChange,
        selectedReceivables, setSelectedReceivables,
        showInteractionForm, setShowInteractionForm,
        showContactForm, setShowContactForm,
        showUndoConfirm, setShowUndoConfirm,
        receivables, history, dvStatus,
        isValidated, showSelectColumn, totals, deals,
        handleCreateContact, handleContactCreated,
        handleAction, handleValidateSelected,
        handleUndoValidation, handleMarkNotConfirmed,
        handleUnmarkNotConfirmed, onCallSuccess, handleInteractionSuccess,
    };
}
